// @flow

/* eslint-disable ta-code-conventions/has-matching-storydoc */

import { actions, type State, type Action } from '@ta/common.state';
import type { Store } from 'redux';
import { shallowEquals } from '@ta/common.object-util';
import ApiCaller from './ApiCaller';

import type {
  BoundComponent,
  ApiEndpoint,
  ApiId,
  JsonPath,
  ApiResponse,
  ApiHandle,
  ContainerParams,
} from './bind';

const JSONPATH_DEPENDENCY = /\{([^}]+):[^}]*\}/;
const countMatches = (str: string, pattern: RegExp) =>
  (str.match(new RegExp(pattern.source, 'g')) || []).length;
const countDependencies = path => countMatches(path, JSONPATH_DEPENDENCY);

const createApiHandle = <T>(id, endpoint, store: Store<State, Action>) => ({
  get: (dependencyId?: ApiId): ApiResponse<T> => {
    const state = store.getState();
    const requestDetails = state.api.requests[id];
    if (!requestDetails) {
      if (DEBUG) {
        return {
          loading: false,
          data: null,
          error: `No request found for ${endpoint}: You should probably move your call to .get() into the function that your staging method returns.`,
        };
      }
      return {
        loading: false,
        data: null,
        error: `No request found for ${endpoint}`,
      };
    }

    const responseToGet = !dependencyId
      ? endpoint
      : endpoint.replace(JSONPATH_DEPENDENCY, `${dependencyId}`);

    const resolvedUrl = requestDetails.result.includes(responseToGet);
    if (!requestDetails.loading && !resolvedUrl) {
      if (DEBUG) {
        return {
          loading: false,
          data: null,
          error: `No request found for ${responseToGet}: the server didn't execute ${endpoint} with ${dependencyId ||
            ''} as the argument.`,
        };
      }
      return {
        loading: false,
        data: null,
        error: `No request found for ${endpoint}`,
      };
    }

    const response = state.api.responses[responseToGet];
    return {
      loading: requestDetails.loading,
      data: response ? response.data : null,
      error: response ? response.error : null,
    };
  },
  select: (path: JsonPath) => `{${id}:${path}}`,
});

/** Function to create a stager for an API call */
export const createStager = (api: ApiCaller, store: Store<State, Action>) => <T>(
  endpoint: ApiEndpoint,
): ApiHandle<T> => {
  if (DEBUG) {
    if (countDependencies(endpoint) > 1) {
      throw new Error(
        `API Calls are limited to one JsonPath dependency. ${endpoint} has too many.`,
      );
    }
    if (!endpoint.startsWith('/')) {
      throw new Error(
        `API Calls should start with a '/' so they aren't affected by the current page url, but ${endpoint} doesn't.`,
      );
    }
  }

  const id = api.getId(endpoint);
  const handle = createApiHandle(id, endpoint, store);
  api.stager(endpoint);
  return ((handle: any): ApiHandle<T>);
};

/** Watches the Redux store and stages any calls needed to satisfy bound API requests */
export default class ApiMonitor {
  store: Store<State, Action>;
  root: BoundComponent<*>;
  previousState: Object;
  param: ?ContainerParams;

  constructor(store: Store<State, Action>, root: BoundComponent<*>, param?: ContainerParams) {
    this.store = store;
    this.root = root;
    this.previousState = this.getState();
    this.param = param;
    store.subscribe(() => this.watch());
  }

  watch() {
    const thisState = this.getState();
    if (!shallowEquals(thisState, this.previousState)) {
      this.previousState = thisState;
      this.reload();
    }
  }

  stage(param: ?ContainerParams): ApiCaller {
    const api = new ApiCaller();
    if (this.root.stage) {
      // $FlowFixMe
      this.root.stage(this.store.getState(), createStager(api, this.store), param);
    }
    return api;
  }

  updateState(param: ?ContainerParams) {
    const api = this.stage(param);
    if (api.hasStagedCalls()) {
      this.store.dispatch(actions.api.newApiRequests(api.stagedCalls));
      return true;
    }
    return false;
  }

  reload(): Promise<boolean> {
    const { param } = this;
    const api = this.stage(param);
    const currentState = this.store.getState().api.requests || {};
    const hasNewRequests = api.filterStagedCalls(
      (key, endpoint) =>
        !(currentState[key] && currentState[key].url && currentState[key].url === endpoint),
    );
    if (hasNewRequests) {
      this.store.dispatch(actions.api.newApiRequests(api.stagedCalls));
      return api.resolve().then(data => {
        this.store.dispatch(actions.api.newApiResponse(data));
        return true;
      });
    }
    return Promise.resolve(true);
  }

  getState(): Object {
    const storeState = this.store.getState();
    return {
      ...storeState,
      api: undefined,
      overlays: undefined,
      rebindCount: (storeState.api && storeState.api.rebindCount) || undefined,
    };
  }
}
